import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Activity,
  ClipboardList,
  Globe,
  KeyRound,
  LayoutDashboard,
  LayoutTemplate,
  Link2,
  ScanSearch,
  Rocket,
  Send,
  Settings,
  ShieldCheck,
  UserCog,
  Users,
} from "lucide-react";
import { useAuthStore } from "@/stores/auth";
import { ShellScaffold, type ShellNavItem } from "@/components/ShellScaffold";
import { useTranslation } from "@/framework-originated/i18n";

// 运营端外壳(layout/AppShell 描述不了全部用例,doc/049 D5):独立 shell,
// 仅暴露运营能力全集。label 硬编码中文不迁移(i18n 记遗留),仅供应商
// 入口走 opsSuppliers 命名空间。
const L: Record<string, string> = {
  dashboard: "总览",
  workbench: "工作区",
  templates: "模板",
  healthCheck: "检测",
  distribute: "分发",
  relayGroup: "中转站",
  relaySites: "站点",
  bindings: "绑定",
  relayTasks: "分发记录",
  pushGroup: "推送站",
  pushSites: "站点",
  pushTasks: "分发记录",
  keys: "上游Key列表",
  settings: "系统设置",
  users: "账号管理",
  usage: "用量",
};

function buildNav(supplierLabel: string, conflictLabel: string, isAdmin: boolean): ShellNavItem[] {
  return [
    { type: "leaf", item: { to: "/", label: L.dashboard, icon: LayoutDashboard } },
    {
      type: "group",
      label: L.workbench,
      icon: LayoutTemplate,
      prefix: "/templates",
      children: [
        { to: "/templates", label: L.templates, icon: LayoutTemplate },
        { to: "/health-check", label: L.healthCheck, icon: ScanSearch },
        { to: "/distribute", label: L.distribute, icon: Send, prefix: true },
      ],
    },
    {
      type: "group",
      label: L.relayGroup,
      icon: Globe,
      prefix: "/relay",
      children: [
        { to: "/relay-sites", label: L.relaySites, icon: Globe },
        { to: "/relay-bindings", label: L.bindings, icon: Link2 },
        { to: "/relay/tasks/list", label: L.relayTasks, icon: ClipboardList },
      ],
    },
    {
      type: "group",
      label: L.pushGroup,
      icon: Rocket,
      prefix: "/push",
      children: [
        { to: "/push-sites", label: L.pushSites, icon: Rocket },
        { to: "/push-bindings", label: L.bindings, icon: Link2 },
        { to: "/push/tasks/list", label: L.pushTasks, icon: ClipboardList },
      ],
    },
    { type: "leaf", item: { to: "/keys", label: L.keys, icon: KeyRound } },
    { type: "leaf", item: { to: "/suppliers", label: supplierLabel, icon: Users, prefix: true } },
    { type: "leaf", item: { to: "/supplier-key-conflicts", label: conflictLabel, icon: ScanSearch } },
    ...(isAdmin
      ? [{ type: "leaf" as const, item: { to: "/users", label: L.users, icon: UserCog } }]
      : []),
    { type: "leaf", item: { to: "/settings", label: L.settings, icon: Settings } },
    { type: "leaf", item: { to: "/usage", label: L.usage, icon: Activity } },
  ];
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { user, token, logout } = useAuthStore();
  const { t } = useTranslation("opsSuppliers");

  useEffect(() => {
    if (!token) navigate({ to: "/login" });
  }, [token, navigate]);

  if (!token) return null;

  return (
    <ShellScaffold
      brandMark={
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-[#2363eb] text-white shadow-[0_7px_20px_#2363eb33]">
          <ShieldCheck size={22} />
        </div>
      }
      brandLabel="BizConsole"
      brandSub="词元供应系统"
      sections={[{ label: "工作台", items: buildNav(t("nav"), t("conflicts.nav"), (user?.role === "admin" || user?.role === "sys")) }]}
      accountName={user?.username ?? "—"}
      accountSub={user?.role ?? "—"}
      onLogout={() => {
        logout();
        navigate({ to: "/login" });
      }}
    >
      {children}
    </ShellScaffold>
  );
}
